import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query"
import type { FnBase, FnParams, FnReturn } from "./types"
import { getArgs } from "./utils/getArgs"

type BaseInitialData<Fn extends FnBase> =
  | FnReturn<Fn>
  | (() => FnReturn<Fn>)
  | undefined

type Options<Fn extends FnBase, TData, InitialData> = Omit<
  UseQueryOptions<FnReturn<Fn>, Error, TData>,
  "queryFn" | "queryKey" | "initialData"
> & {
  initialData?: InitialData
}

export type UseQuery<Fn extends FnBase> = <
  TData = FnReturn<Fn>,
  InitialData extends BaseInitialData<Fn> = undefined
>(
  args: FnParams<Fn>,
  options?: Options<Fn, TData, InitialData>
) => {
  data: UseQueryResult<TData>["data"]
  isError: UseQueryResult["isError"]
  error: UseQueryResult["error"]
  isLoading: UseQueryResult["isLoading"]
  isPending: UseQueryResult["isPending"]
}

export const getUseQuery = (fn: FnBase, path: string[]): UseQuery<FnBase> => {
  return (...input) => {
    const { args = [], rest } = getArgs(input)
    const [options] = rest
    const key = [...path, ...args]

    const result = useQuery<any>({
      // TODO [error-handling-effort] https://jira.attensi.com/browse/WEB-18497
      throwOnError: true,
      ...options,
      queryKey: key,
      queryFn: () => fn(...args),
    })

    const { data, isError, error, isLoading, isPending } = result

    return { data, isError, error, isLoading, isPending }
  }
}
